/**
 * Exports, PLAN.md §11.
 *
 * Everything here is client-side: a Blob download for `.md`, the clipboard for
 * copy, and `window.print()` against the `@media print` rules in `index.css`
 * for PDF. No pandoc, no headless Chrome, and the PDF uses the same fonts the
 * screen does.
 */

/** "2026-03-14-linear-algebra.md" - sortable first, readable second. */
export function exportFilename(
  subject: string,
  date: string,
  extension = "md",
): string {
  const slug = subject
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return `${date}${slug === "" ? "" : `-${slug}`}.${extension}`;
}

/**
 * `navigator.clipboard` needs a secure context and rejects when the document
 * is not focused, both of which happen in practice - hence the fallback.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const area = document.createElement("textarea");
      area.value = text;
      area.setAttribute("readonly", "");
      area.style.position = "fixed";
      area.style.opacity = "0";
      document.body.appendChild(area);
      area.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(area);
      return ok;
    } catch {
      return false;
    }
  }
}

export function downloadText(
  filename: string,
  text: string,
  mimeType = "text/markdown;charset=utf-8",
): void {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoking immediately races the download in Safari; a tick is enough.
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}

/**
 * The print title becomes the PDF's default filename in every browser that
 * offers "Save as PDF", so it is set for the duration of the dialog.
 */
export function printDocument(title?: string): void {
  if (title === undefined) {
    window.print();
    return;
  }
  const previous = document.title;
  document.title = title;
  window.print();
  // Chrome fires afterprint synchronously enough that a timeout is the only
  // ordering that survives both it and Firefox.
  setTimeout(() => {
    document.title = previous;
  }, 500);
}

/** "1:04:12" / "12:30" - the same shape the recorder's elapsed clock uses. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1_000));
  const hours = Math.floor(total / 3_600);
  const minutes = Math.floor((total % 3_600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(minutes)}:${pad(seconds)}`;
}

/**
 * The transcript as Markdown: one line per segment, prefixed by its start
 * time, so a timestamp in the export can be found again in the player.
 */
export function transcriptMarkdown(
  title: string,
  segments: { startMs: number; text: string }[],
): string {
  const lines = segments
    .map((segment) => ({ at: formatClock(segment.startMs), text: segment.text.trim() }))
    .filter((segment) => segment.text !== "")
    .map((segment) => `**[${segment.at}]** ${segment.text}`);
  return `# ${title}\n\n${lines.join("\n\n")}\n`;
}
